import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CommonRootState } from '@common/types/common-root-state-type';
import { createSelector } from '@reduxjs/toolkit';
import { isExtensionContext, isWhatsappExtensionContext } from '@common/services/detect-context';
import Bowser from 'bowser';
import { sessionSelectOneObjectByQuery } from './DBServiceThunks';
import { isDisplayConnected } from './engine-display-status';
import { NotifierConnectionStatus } from '@/common/noifier-module/types';
import type { WaPairing } from '@common/noifier-module/wa-pairing';
import { isAlwaysOn, type EngineKind } from '@bt-common/notifier/presence';
import { BAILEYS_SUPPORTED_COMMANDS, CHROME_SUPPORTED_COMMANDS, WIRE_CONTRACT_VERSION } from '@bt-common/interfaces/whatsapp-tool-contracts.interface';

export interface ConnectedSocket {
  id: string;
  user?: {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  device: string;
  /** Rollout window only — read in ONE place, selectOnlineEngines. @7.12 delete. */
  browserType: string;
  connectionTime: string;
  connectedFrom: string;
  domain?: string;
  // MQTT presence specific fields
  clientId?: string;
  app?: string;
  ts?: number;
  ip?: string;
  /** True while this client has the team-inbox view mounted (MQTT presence). */
  teamInboxOpen?: boolean;
  /** Extension presence: is its WhatsApp Web linked? Absent on an older extension. */
  waPairing?: WaPairing;
  /** EnginePresence (plan A.9.5): absent on the web app's own presence and on
   *  an engine older than the contract. */
  engineKind?: EngineKind;
  commands?: string[];
  contractVersion?: number;
}

/** An online WhatsApp engine, read from its presence record. */
export interface OnlineEngine {
  id: string;
  engineKind: EngineKind;
  /** The commands it serves (`EnginePresence.commands`). */
  commands: readonly string[];
  /** No `contractVersion`, or one below this build's: "online, update required". */
  updateRequired: boolean;
  app?: string;
  connectionTime: string;
  teamInboxOpen?: boolean;
  /** Extension only: is its WhatsApp Web linked? Undefined = not stated (older extension). */
  waPairing?: WaPairing;
}

interface WhatsAppEngine {
  id: string;
  type: 'whatsapp';
  name: string;
  domain: string;
  connectionTime: string;
}

type Engine = ConnectedSocket | WhatsAppEngine | OnlineEngine;

interface DeliveryStatus {
  color: string;
  text: string;
  // Whether this status represents an established/in-progress WhatsApp
  // connection (gateway ready/QR/starting, or a live WhatsApp Web socket).
  // Single source of truth for "is the user connected" — read by the status
  // pill AND by nudges via `hasWhatsAppConnection`.
  connected: boolean;
  alert: {
    type: 'error' | 'warning';
    message: string;
  } | null;
}

export interface ConnectedEnginesState {
  sockets: ConnectedSocket[];
}

interface ConnectedEnginesDisplayState {
  engines: Engine[];
  status: DeliveryStatus;
  hasGateway: boolean;
  isGatewayConnected: boolean;
  isNotifierConnected: boolean;
  // True when any engine the status pill treats as connected is present:
  // a live/QR/starting gateway session OR a live WhatsApp Web socket. This is
  // the same set of conditions that produce a non-"No WhatsApp Connected"
  // status below, exposed so nudges can hide when the
  // user is actually connected — even without a gateway session doc.
  hasWhatsAppConnection: boolean;
  // A live LOCAL engine: this tab is WhatsApp Web, or an extension socket is
  // online elsewhere. Independent of any gateway — both can be true at once.
  hasWhatsAppWeb: boolean;
}

const initialState: ConnectedEnginesState = {
  sockets: []
};

export const connectedEnginesSlice = createSlice({
  name: 'connectedEngines',
  initialState,
  reducers: {
    updateConnectedSockets: (state, action: PayloadAction<ConnectedSocket[]>) => {
      state.sockets = action.payload;
    },
    // setAllClients removed - use upsertClient/removeClient for presence tracking
    upsertClient: (state, action: PayloadAction<ConnectedSocket>) => {
      const index = state.sockets.findIndex(socket => socket.id === action.payload.id);
      if (index !== -1) {
        state.sockets[index] = action.payload;
      } else {
        state.sockets.push(action.payload);
      }
    },
    removeClient: (state, action: PayloadAction<{ clientId: string }>) => {
      state.sockets = state.sockets.filter(socket => socket.id !== action.payload.clientId);
    }
  }
});

// Selectors
// Memoize this selector to reduce unnecessary calls during dependency checks
let selectConnectedSocketsLastResult: ConnectedSocket[] | null = null;
let selectConnectedSocketsLastStateRef: any = null;

export const selectConnectedSockets = (state: CommonRootState) => {
  const stateRef = state.ConnectedEnginesReducer;
  const sockets = stateRef?.sockets || [];

  // Only recompute when state reference actually changes
  if (selectConnectedSocketsLastStateRef !== stateRef) {
    selectConnectedSocketsLastStateRef = stateRef;
    selectConnectedSocketsLastResult = sockets;
  }

  return selectConnectedSocketsLastResult || sockets;
};

/**
 * THE online engines (plan A.9.5): the presence records of WhatsApp engines —
 * the extension, a Chrome gateway, a Baileys pod. The web app's own presence is
 * not an engine. Every engine-aware consumer reads this, never `browserType`
 * or a clientId prefix.
 */
export const selectOnlineEngines = createSelector([selectConnectedSockets], (sockets): OnlineEngine[] =>
  sockets.flatMap((s): OnlineEngine[] => {
    if (!s?.id) return [];
    // @7.12 — an engine older than the contract publishes no engineKind: read
    // its browserType and the manifest of its kind, as before the contract.
    // The ONLY browserType reading; deleted with browserType in 7.12.
    const legacyKind: EngineKind | undefined = s.engineKind ? undefined
      : s.browserType === 'WhatsApp Web' ? 'extension'
      : s.browserType === 'Gateway' ? 'chrome-gateway'
      : s.browserType === 'Baileys' ? 'baileys'
      : undefined;
    const engineKind = s.engineKind ?? legacyKind;
    if (!engineKind) return [];
    const commands = s.commands
      ?? Object.keys(engineKind === 'baileys' ? BAILEYS_SUPPORTED_COMMANDS : CHROME_SUPPORTED_COMMANDS);
    return [{
      id: s.id,
      engineKind,
      commands,
      updateRequired: typeof s.contractVersion !== 'number' || s.contractVersion < WIRE_CONTRACT_VERSION,
      app: s.app,
      connectionTime: s.connectionTime,
      teamInboxOpen: s.teamInboxOpen,
      waPairing: s.waPairing,
    }];
  }),
);

/**
 * The commands a WhatsApp action can use right now (plan A.9.5 — a command is
 * supported exactly when an engine lists it): in the extension's own tab, the
 * extension's manifest (its own presence is not in the list); elsewhere, the
 * union of the online engines' `commands`.
 */
export const selectServedCommands = createSelector([selectOnlineEngines], (engines): readonly string[] =>
  isWhatsappExtensionContext()
    ? Object.keys(CHROME_SUPPORTED_COMMANDS)
    : Array.from(new Set(engines.flatMap((e) => e.commands))),
);

// Selector to track location hostname for proper memoization
// Note: This selector reads window.location directly, so it won't trigger Redux recomputation
// when location changes. Use makeSelectConnectedEnginesState with hostname parameter instead.
export const selectLocationHostname = (state: CommonRootState) => {
  return typeof window !== 'undefined' ? window.location.hostname : '';
};

// Factory selector that accepts hostname to properly track location changes.
// Cache the selector instance per hostname to avoid creating new selectors
// on every call. Typed against CommonRootState (matches selectConnectedSockets
// above) and the concrete return type so call sites get the real shape rather
// than `unknown` — `ReturnType<typeof createSelector>` resolves to a union
// that collapses to `unknown` under TS 5+.
type EngineStateSelector = (state: CommonRootState) => ConnectedEnginesDisplayState;
const selectorCache = new Map<string, EngineStateSelector>();

export const makeSelectConnectedEnginesState = (hostname: string) => {
  // Return cached selector if it exists for this hostname
  const cached = selectorCache.get(hostname);
  if (cached) {
    return cached;
  }

  const selector = createSelector(
    [
      selectOnlineEngines,
      (state: CommonRootState) => state.NotifierReducer?.connectionStatus,
      (state: CommonRootState) => sessionSelectOneObjectByQuery(state.DBSessionReducer || {}, {
        // Include transient post-connected states (OPENING / stream_disconnected /
        // disconnected) so a brief WA-state blip doesn't null out activeSession and
        // flip the header straight to "No WhatsApp Connected". The lastSeen
        // freshness gate below decides whether to actually show connected.
        status: { $in: ["connected", "qrcode", "unpaired", "syncing", "OPENING", "stream_disconnected", "disconnected"] }
      }),
      // A minute-resolution clock input: the connectedAt grace window is time-based, so a
      // pod that dies silently must still age out here without breaking memoization.
      // (Elsewhere the caller passes a throttled `now` via selectNow — engine-display-status;
      // this selector's many callers take no argument, hence the internal bucket.)
      () => Math.floor(Date.now() / 60_000),
    ],
    (onlineEngines, connectionStatus, activeSession, nowMinute): ConnectedEnginesDisplayState => {
    
    // Helper function to check if notifier is still valid
    const isNotifierConnected = connectionStatus === NotifierConnectionStatus.AUTHENTICATED;

    // Check if we're currently on WhatsApp Web - if extension context, we're definitely on WhatsApp Web
    const isCurrentlyOnWhatsAppWeb = hostname === 'web.whatsapp.com';

    // Add current extension as a socket if we're in extension context
    const currentExtensionSocket: ConnectedSocket | null = isExtensionContext() ? {
      id: 'current-extension',
      device: 'Desktop',
      browserType: Bowser.getParser(window.navigator.userAgent).getBrowser().name || 'Unknown',
      connectionTime: new Date().toISOString(),
      connectedFrom: 'webapp',
      domain: hostname === 'web.whatsapp.com' ? 'web.whatsapp.com' : 'website'
    } : null;

    // Combine all engines
    const allEngines: Engine[] = [
      ...(currentExtensionSocket ? [currentExtensionSocket] : []),
      ...onlineEngines,
      ...(activeSession && activeSession._id ? [{
        id: activeSession._id,
        type: 'whatsapp' as const,
        name: 'WhatsApp Server',
        domain: 'gateway',
        connectionTime: activeSession.updatedAt?.toString() || new Date().toString()
      }] : [])
    ];

    // Determine engine types and session status
    const hasGateway = activeSession !== null || onlineEngines.some(isAlwaysOn);
    
    // Same rule as the Engines list and the top-bar indicator (engine-display-status): anchored
    // to the connectedAt grace window, so upgrade / spot-reclaim churn doesn't flip it and a
    // dead pod whose doc still says 'connected' ages out — one definition, so the header, the
    // /connections guide and the table rows never disagree.
    const isGatewayConnected = isDisplayConnected(activeSession, nowMinute * 60_000);
    const isGatewayInQRMode = activeSession?.status === 'qrcode';
    const isGatewayInSetup = activeSession && ['unpaired', 'syncing', 'initializing', 'browser_started', 'navigating'].includes(activeSession.status || '');
    
    const hasWhatsAppWeb = isCurrentlyOnWhatsAppWeb || onlineEngines.some((e) => e.engineKind === 'extension');

    // Presence alone only proves the browser tab is open. The extension also states whether its
    // WhatsApp Web is linked, so a logged-out / QR tab must not read as a working browser engine.
    // Only when EVERY extension says logged out (one that is linked, or doesn't say, keeps the old
    // reading), and not when this very tab is WhatsApp Web (its own state isn't in the list).
    const extensions = onlineEngines.filter((e) => e.engineKind === 'extension');
    const isBrowserLoggedOut = !isCurrentlyOnWhatsAppWeb
      && extensions.length > 0
      && extensions.every((e) => e.waPairing === 'logged_out');


    // Calculate delivery status directly (not as a function)
    let status: DeliveryStatus;
    
    if (!isNotifierConnected) {
      status = {
        color: '#ef4444',
        text: 'Not Connected',
        connected: false,
        alert: {
          type: 'error',
          message: 'The notification service is not connected. Messages may not be delivered.'
        }
      };
    } else if (isGatewayConnected) {
      status = {
        color: '#22c55e',
        text: 'WhatsApp Ready (Offline OK)',
        connected: true,
        alert: null
      };
    } else if (isGatewayInQRMode) {
      status = {
        color: '#f97316',
        text: 'Scan QR Code to Connect',
        connected: true,
        alert: {
          type: 'warning',
          message: 'WhatsApp gateway is ready but needs QR code scan to complete setup.'
        }
      };
    } else if (isGatewayInSetup) {
      status = {
        color: '#f97316',
        text: 'WhatsApp Gateway Starting...',
        connected: true,
        alert: {
          type: 'warning',
          message: 'WhatsApp gateway is starting up. Please wait a moment.'
        }
      };
    } else if (isBrowserLoggedOut) {
      status = {
        color: '#ef4444',
        text: 'WhatsApp Logged Out',
        connected: false,
        alert: {
          type: 'error',
          message: 'WhatsApp Web is logged out. Open WhatsApp Web and scan the QR code so messages can be sent.'
        }
      };
    } else if (hasWhatsAppWeb || isCurrentlyOnWhatsAppWeb) {
      status = {
        color: '#f97316',
        text: 'WhatsApp Web Only',
        connected: true,
        alert: {
          type: 'warning',
          message: 'Keep WhatsApp Web open in your browser for messages to be sent.'
        }
      };
    } else {
      status = {
        color: '#ef4444',
        text: 'No WhatsApp Connected',
        connected: false,
        alert: {
          type: 'error',
          message: 'Messages cannot be sent. Connect WhatsApp to enable message delivery.'
        }
      };
    }

    // Single source of truth: the status ladder above already classifies every
    // connection state, so read connectivity straight off it rather than
    // re-deriving the conditions. Deliberately independent of a gateway session
    // doc — a WhatsApp-Web-only user has a live socket but no session.
    const hasWhatsAppConnection = status.connected;

    return {
      engines: allEngines,
      status,
      hasGateway,
      isGatewayConnected,
      isNotifierConnected,
      hasWhatsAppConnection,
      hasWhatsAppWeb
    };
    }
  );

  // Cache the selector for this hostname
  selectorCache.set(hostname, selector);
  return selector;
};

// Support-specific selector that only shows real EMQX engines, no artificial ones
export const selectConnectedEnginesSupport = createSelector(
  [selectConnectedSockets],
  (connectedSockets): ConnectedSocket[] => {
    // Only return actual connected sockets from EMQX API
    // Filter out any invalid or incomplete sockets
    return connectedSockets.filter((socket) => !!socket?.id);
  }
);

export const { updateConnectedSockets, upsertClient, removeClient } = connectedEnginesSlice.actions;

export default connectedEnginesSlice.reducer;

// Unified selector that handles all engine detection and status logic
// This is a fallback that uses current window.location.hostname
// For proper location tracking, use makeSelectConnectedEnginesState with hostname parameter
export const selectConnectedEnginesState = makeSelectConnectedEnginesState(
  typeof window !== 'undefined' ? window.location.hostname : ''
);

/** How the user is connected right now. A live gateway wins: it's the connection
 *  that keeps sending with the computer off. */
export type ConnectionKind = 'remote' | 'local' | 'none';

export function connectionKind(engines: { isGatewayConnected: boolean; hasWhatsAppWeb: boolean }): ConnectionKind {
  if (engines.isGatewayConnected) return 'remote';
  if (engines.hasWhatsAppWeb) return 'local';
  return 'none';
}

export const selectConnectionKind = createSelector([selectConnectedEnginesState], connectionKind);

// ── Team-inbox presence ──────────────────────────────────────────────────
// Presence heartbeats publish every 30s but Chrome throttles hidden-tab
// timers to ~1/min, so allow up to two throttled beats to go missing before
// declaring the tab gone. Explicit offline (tab close → LWT/unmount) removes
// the socket immediately, so this window only matters for crashed tabs.
export const TEAM_INBOX_PRESENCE_FRESHNESS_S = 150;

export const selectIsTeamInboxOpenSomewhere = (state: CommonRootState): boolean => {
  const nowS = Math.floor(Date.now() / 1000);
  return (state.ConnectedEnginesReducer?.sockets ?? []).some(
    (s) =>
      s.teamInboxOpen === true &&
      typeof s.ts === 'number' &&
      nowS - s.ts < TEAM_INBOX_PRESENCE_FRESHNESS_S,
  );
};
